/**
 * «Продление песни» — связка интерфейса с ядром.
 *
 * Два режима, потому что задачи РАЗНЫЕ:
 *   · «Длина песни» — пересборка трека повтором (или пропуском) его собственных
 *     тактов до нужной длины: analyze → plan → render. Автоматический режим.
 *   · «Повторить участок» — ручной: человек выделяет кусок, и он звучит N раз
 *     подряд, остальная песня на месте (обращение 22.10.2026 — «выход» дважды
 *     для выступления). Края встают на первую долю такта (plan.js, repeatRegion).
 *     Там же второе действие — «Растянуть»: вокальный приём из заявки #26,
 *     кусок тянется во времени и по желанию последними звуками уходит вверх.
 *
 * ── Переделка 16.10.2026 (обращение #1910: «непонятно как пользоваться…
 *    результат как игра в рулетку… познаётся методом тыка») ──────────────────
 * Прежний порядок был «задай длину → нажми → посмотри, что вышло». Телеметрия
 * RU за 30 дней: 734 сессии с прогоном «Всей песни» дали 2016 прогонов — люди
 * подбирали длину перебором вслепую, потому что план был виден только ПОСЛЕ
 * сборки, а варианты подписаны звёздами. Теперь:
 *   1. анализ стартует сам, как только файл прочитан;
 *   2. план и сборка пересчитываются на КАЖДУЮ правку длины — сборка из
 *      готовых тактов стоит миллисекунды, ждать нажатия нечего;
 *   3. волна результата окрашена цветом места в оригинале, а на оригинале
 *      отмечено, что прозвучит дважды или выпадет — план читается глазами;
 *   4. варианты названы по признаку («меньше швов», «длинными частями»),
 *      швы словами («незаметны»), а не звёздами (describe.js);
 *   5. «Результат / Оригинал» переключает звук с того же места музыки.
 * Счётчик дня тратится при СКАЧИВАНИИ: слушать и перебирать — бесплатно
 * (CLAUDE.md: предпросмотр не списывается, списывает выданный результат).
 */

import { analyzeAsync } from './analyze.js';
import { planVariants, planRepeat } from './plan.js';
import { renderSegments, planToRanges } from './render.js';
import { stretchRegion } from './stretch-region.js';
import { Waveform, placeColor, isLight } from './waveform.js';
import { describeVariants, rangesToMap, outToSrc, srcToOut, seamQuality } from './describe.js';
import { Transport, makeAudioBuffer } from './transport.js';

const SLUG = 'extend-song';
/* Потолок длины. До 16.10.2026 был ×6 — «дальше повторы станут слышны», и в
 * него за 30 дней упёрлись 52 сессии (144 отказа): короткий кусок, из которого
 * нужна песня на несколько минут. Замер того же дня (8 песен корпуса, обрезанных
 * до 30 и 60 с): план находится в 15–16 из 16 до ×10, промах длины медиана
 * 0,7–1,3 с, худший 1,7 с — в пределах прежнего худшего. Качество швов зависит
 * от материала, а не от кратности: 30-секундный кусок даёт «могут быть слышны»
 * уже на ×4. Поэтому за ×6 не отказ, а честная строка о повторах; за ×10 промах
 * длины растёт до 4,7 с (тишина в конце) — там отказ остаётся. */
const MAX_MUL = 10;
const CAUTION_MUL = 6;
const MIN_MUL = 0.33;    // короче трети пропадают целые куплеты (замер волны 3)

const S = {
    file: null,
    buffer: null,          // исходник (AudioBuffer)
    analysis: null,
    analyzing: null,       // Promise анализа
    mode: 'song',          // 'song' | 'region'
    regionAct: 'repeat',   // что делать с участком: 'repeat' | 'stretch'
    target: 0,
    variants: null,
    descs: null,
    variantIndex: 0,
    excluded: new Set(),   // швы, убранные руками ('from:to')
    song: null,            // результат режима «Длина песни»
    region: null,          // {start,end} выделение
    regionResult: null,    // результат «Растянуть»
    repeat: null,          // план «Повторить» (repeatRegion), края уже на тактах
    repeatResult: null,    // результат «Повторить»
    repeatNear: null,      // участок рядом, где шов ровнее (repeatSuggestion)
    repeatExact: false,    // долей нет — участок как выделен
    pendingHint: null,     // участок, приехавший с файлом из другого тула
    view: 'out',           // что играет и что подсвечено: 'out' | 'src'
    busy: false,           // экспорт или растяжение
    format: 'mp3',
    activeSeam: -1,
    planTimer: 0,
    planVia: 'auto',
};

const $ = (id) => document.getElementById(id);
const I18N = () => window.__ES_I18N || {};
const t = (key, fb) => I18N()[key] || fb || key;
const ae = (name, data) => { try { window.__aeFunnel && window.__aeFunnel(name, { slug: SLUG, ...data }); } catch (_) {} };
const stage = (n) => { try { window.__stage && window.__stage(n); } catch (_) {} };
// Запись в DOM из цикла кадров — через общие примитивы (пишут, только когда
// значение правда сменилось), с местной заменой для страницы со старым ядром.
function pxWrite(e, p, v, u) { if (window.__perPixel) return window.__perPixel(e, p, v, u); e.style[p] = v + (u || '%'); }
function textWrite(e, t) { if (window.__setText) return window.__setText(e, t); e.textContent = t; }
const put = (el, text) => { if (el) textWrite(el, text == null ? '' : String(text)); };

/* ── Форматирование ──────────────────────────────────────────────────────── */

function fmt(sec) {
    if (!isFinite(sec) || sec < 0) sec = 0;
    const m = Math.floor(sec / 60);
    const s = Math.round(sec - m * 60);
    if (s === 60) return (m + 1) + ':00';
    return m + ':' + String(s).padStart(2, '0');
}
/** Длина цели в поле: дробная (взята из видео 0:37,4) — с десятой, иначе м:сс. */
function fmtTarget(sec) {
    if (!isFinite(sec) || sec < 0) return fmt(0);
    const tenths = Math.round(sec * 10) / 10;
    if (Math.abs(tenths - Math.round(tenths)) < 0.05) return fmt(Math.round(tenths));
    const m = Math.floor(tenths / 60);
    const s = tenths - m * 60;
    return m + ':' + (s < 10 ? '0' : '') + num(s, 1);
}
/** Дробное число по правилам языка страницы (ru — «1,5», en — «1.5»). */
function num(x, digits = 1, min = digits) {
    try {
        return new Intl.NumberFormat(window.__intlLocale ? window.__intlLocale() : undefined, { minimumFractionDigits: min, maximumFractionDigits: digits }).format(x);
    } catch (_) { return x.toFixed(digits); }
}
const LRI = '⁦', PDI = '⁩';   // изолят «слева направо» для времени в RTL
function fmtDelta(d) { return (d >= 0 ? '+' : '−') + fmt(Math.abs(d)); }
function fmtMul(m) { return '×' + num(Math.round(m * 10) / 10, 1, 0); }

/** Число со словом: формы приходят из перевода по категориям Intl.PluralRules. */
function plural(key, n) {
    const forms = I18N()[key];
    let cat = 'other';
    try { cat = new Intl.PluralRules(window.__intlLocale ? window.__intlLocale() : 'ru').select(n); } catch (_) {}
    const f = forms && (forms[cat] || forms.other || forms.many);
    return (f || ':n').replace(':n', String(n));
}

// Длина — по общему разбору времени (public/js/time-field.js): «3,30» — это 3:30, а не 3,3 с.
function parseTime(str) {
    const v = window.TimeField ? window.TimeField.parse(str) : null;
    return v === null ? NaN : v;
}

/** Длина из `?target=` (м:сс) — или 0. Сюда ведёт /speed («ровно 2 минуты»). */
function linkTarget() {
    try {
        const raw = new URLSearchParams(location.search).get('target');
        const v = raw ? parseTime(raw) : NaN;
        return isFinite(v) && v > 0 ? v : 0;
    } catch (_) { return 0; }
}

function channelsOf(buffer) {
    const out = [];
    for (let c = 0; c < buffer.numberOfChannels; c++) out.push(buffer.getChannelData(c));
    return out;
}

function errLabel(err) {
    if (!err) return 'unknown';
    const n = err.code || (err.name && err.name !== 'Error' ? err.name : '');
    if (n) return String(n).slice(0, 40);
    return String(err.message || 'Error').replace(/\s+/g, ' ').slice(0, 60);
}

/* ── Прогресс ──────────────────────────────────────────────────────────────
 * Общая панель (components/tool-progress): процент, остаток, часы, «живо».
 * Своя полоса здесь уже была — без единого из этих четырёх, и на экспорте она
 * замирала на 50 % (жалоба 30.08: «Почему музыка не скачивается?»). */
const panel = (id) => (window.__toolProgress ? window.__toolProgress.get(id) : null);
let runLive = false;
function setStatus(text, pct) {
    const p = panel('es-run-progress');
    if (!p) return;
    if (!text) { runLive = false; p.hide(); return; }
    if (!runLive) { runLive = true; p.start(text, { indeterminate: typeof pct !== 'number' }); }
    else p.label(text);
    if (typeof pct === 'number') p.set(pct);
}

/* Пока идёт экспорт или растяжение, каждое действие тула выглядит занятым, а
 * не молчит: 71 «клик в пустоту» по «Скачать MP3» в жалобе 30.08 — это кнопки,
 * которые на вид были исправны. */
function syncBusy() {
    const off = !!S.busy;
    for (const id of ['es-minus', 'es-plus', 'es-target']) { const el = $(id); if (el) el.disabled = off; }
    const st = $('es-stretch');
    if (st) st.disabled = off || !S.region;
    const dl = $('es-dl');
    if (dl) dl.disabled = off || !current();
    document.querySelectorAll('.es-variant,.es-seam-x,[data-es-mode],[data-es-act],.es-chip,.es-vid,[data-es-fmt],#es-times').forEach((el) => { el.disabled = off; });
    document.querySelectorAll('.es-nudge').forEach((el) => { el.disabled = off || !S.repeat || S.repeatExact; });
    const work = $('es-work');
    if (work) work.toggleAttribute('data-busy', off);
}
function setBusy(on) { S.busy = on; syncBusy(); }

/* ── Кодировщики ─────────────────────────────────────────────────────────── */

function makeBuffer(channels, sampleRate) {
    return {
        numberOfChannels: channels.length, sampleRate,
        length: channels[0].length, duration: channels[0].length / sampleRate,
        getChannelData: (i) => channels[i],
    };
}

function wavEncoder() {
    if (window.MediaPipelineAudioNative && window.MediaPipelineAudioNative.audioBufferToWav) return window.MediaPipelineAudioNative.audioBufferToWav;
    if (window.MediaPipeline && window.MediaPipeline.audio && window.MediaPipeline.audio.audioBufferToWav) return window.MediaPipeline.audio.audioBufferToWav;
    if (window.AudioPipeline && window.AudioPipeline.audioBufferToWav) return window.AudioPipeline.audioBufferToWav;
    return null;
}

/** Кодировщики и AudioPipeline.stretch грузятся лениво — без ожидания и
 *  скачивание, и «Участок» падали с «WAV encoder unavailable». */
async function ensurePipeline() {
    if (wavEncoder() && window.AudioPipeline && window.AudioPipeline.stretch) return;
    try {
        if (window.ensureMediaPipeline) await window.ensureMediaPipeline();
        if (window.ensureAudioPipeline) await window.ensureAudioPipeline();
    } catch (_) { /* дальше — по фактическому наличию */ }
}

/* ── Точно в длительность ─────────────────────────────────────────────────
 * Склейка по тактам промахивается на долю такта: замер 08.10.2026 (34 песни ×
 * 10 целей от ×0.6 до ×2) — медиана 0,3–0,5 с, худший 3 с. Добираем остаток,
 * по возможности не трогая музыку:
 *   · вышло короче — тишина в конце (музыка не меняется вовсе);
 *   · длиннее, а срезаемый хвост тихий (пик ниже −45 dBFS) — срез;
 *   · хвост звучит — равномерное ускорение всего результата: ×1.003 — PQ −0.009,
 *     ×1.01 — −0.013 (Audiobox, 34 песни); заметность темпа начинается с 3 %.
 * Решение (`landKind`) считается при каждой сборке — его видно под кнопкой; само
 * доведение (`landExactly`) — при скачивании: вокодер идёт 5–37 с.
 * Разбор — docs/why/speed-target-length.md. */
const LAND_MAX = 0.03;
const QUIET_TAIL_DBFS = -45;

function landKind(channels, sr, target) {
    const want = Math.round(target * sr);
    const have = channels[0].length;
    if (!(want > 0) || Math.abs(want - have) <= 1) return { how: 'exact' };
    if (want > have) return { how: 'pad', sec: (want - have) / sr };
    let peak = 0;
    for (const c of channels) for (let i = want; i < have; i++) { const a = c[i] < 0 ? -c[i] : c[i]; if (a > peak) peak = a; }
    // Скорость в пределах 0,1 % вокодер считает единицей и отдаёт вход как есть
    // (замер 08.10: ×1.0001 — промах 23 мс остался): такой остаток режется.
    const tiny = (have - want) <= Math.max(0.1 * sr, 0.001 * have);
    if (tiny || 20 * Math.log10(peak + 1e-12) < QUIET_TAIL_DBFS) return { how: 'trim', sec: (have - want) / sr };
    const rate = have / want;
    if (rate - 1 > LAND_MAX) return { how: 'skip', rate };
    return { how: 'stretch', rate };
}

/**
 * Довести собранный результат до цели к скачиванию. Без лишних копий трека:
 * тишина в конце уже лежит в буфере (renderVariant выделяет его длиной цели),
 * срез — вид на тот же буфер, и только ускорение — новый проход вокодера.
 */
async function landExactly(res, target, opts = {}) {
    const sr = res.sampleRate;
    const want = Math.round(target * sr);
    const k = res.landed || landKind(res.channels, sr, target);
    if (k.how === 'trim') {
        // Затухание 50 мс на месте среза пишется в сам буфер — один раз: после
        // него до конца буфера только тихий хвост (ниже −45 dBFS) или доли
        // секунды, и предпрослушка от этого не меняется на слух.
        if (!res.trimFaded) {
            const fade = Math.min(Math.round(0.05 * sr), want);
            for (const c of res.channels) for (let i = 0; i < fade; i++) c[want - fade + i] *= 1 - (i + 1) / fade;
            res.trimFaded = true;
        }
        return res.channels.map((c) => c.subarray(0, want));
    }
    if (k.how !== 'stretch' || !window.AudioPipeline || !res.audio) return res.channels;
    const out = await window.AudioPipeline.stretch(res.audio, { rate: k.rate, semitones: 0, content: 'music', slug: SLUG, signal: opts.signal || null, onProgress: opts.onProgress || null });
    // Длина — до сэмпла: вокодер выдаёт round(N / скорость), но доверять — не значит не проверять.
    const chans = [];
    for (let i = 0; i < out.numberOfChannels; i++) {
        const d = out.getChannelData(i);
        if (d.length === want) chans.push(d);
        else { const o = new Float32Array(want); o.set(d.length > want ? d.subarray(0, want) : d); chans.push(o); }
    }
    return chans;
}

/* ── Дорожки, транспорт ─────────────────────────────────────────────────── */

let waveIn = null, waveOut = null;

const transport = new Transport({
    onTick: (pos, which) => paintPlayhead(pos, which),
    onState: (on) => syncPlayButton(on),
});

function current() {
    if (S.mode === 'song') return S.song;
    return S.regionAct === 'repeat' ? S.repeatResult : S.regionResult;
}

/** Курсоры: на играющей дорожке — сплошной, на другой — «тень»: где в
 *  оригинале звучащее сейчас место результата (и наоборот). */
function paintPlayhead(pos, which) {
    const res = current();
    const srcDur = S.buffer ? S.buffer.duration : 0;
    const outDur = res ? res.duration : 0;
    let tOut, tSrc;
    if (which === 'out') { tOut = pos; tSrc = res ? outToSrc(res.map, pos) : 0; }
    else { tSrc = pos; tOut = res ? srcToOut(res.map, pos) : 0; }
    const phIn = $('es-ph-src'), phOut = $('es-ph-out');
    const pctIn = srcDur ? Math.min(100, (tSrc / srcDur) * 100) : 0;
    const pctOut = outDur ? Math.min(100, (tOut / outDur) * 100) : 0;
    if (phIn) pxWrite(phIn, 'left', pctIn);
    if (phOut) pxWrite(phOut, 'left', pctOut);
    if (phIn) phIn.classList.toggle('is-ghost', which !== 'src');
    if (phOut) phOut.classList.toggle('is-ghost', which !== 'out');
    put($('es-time'), fmt(pos));
    put($('es-total'), fmt(which === 'out' ? outDur : srcDur));
}

function syncPlayButton(on) {
    const b = $('es-play');
    if (!b) return;
    b.classList.toggle('is-on', !!on);
    const label = on ? t('pause', 'Пауза') : t('play', 'Слушать');
    b.setAttribute('aria-label', label);
    b.title = label;
    document.querySelectorAll('.es-seam').forEach((el) => {
        const i = +el.getAttribute('data-i');
        el.classList.toggle('is-on', !!on && transport.until != null && i === S.activeSeam);
    });
}

function setView(which, via) {
    if (which === 'out' && !current()) which = 'src';
    const prev = S.view;
    S.view = which;
    document.querySelectorAll('[data-es-view]').forEach((b) => {
        const on = b.getAttribute('data-es-view') === which;
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    const work = $('es-work');
    if (work) work.setAttribute('data-view', which);
    if (prev === which) return;
    const res = current();
    // Та же точка МУЗЫКИ на другой дорожке.
    const pos = transport.position();
    let mapped = pos;
    if (res) mapped = which === 'src' ? outToSrc(res.map, pos) : srcToOut(res.map, pos, 0);
    transport.switchTo(which, mapped);
    if (via) ae('ab_switch', { to: which, via });
}

/* ── Цели, сводка, сообщения ─────────────────────────────────────────────── */

function readTarget() { return parseTime($('es-target').value); }

function targetProblem(target) {
    const src = S.buffer.duration;
    if (!isFinite(target) || target <= 0) return t('err_target', 'Укажите длительность в формате 3:30 или числом секунд.');
    if (Math.abs(target - src) < 1) return t('err_same', 'Цель совпадает с исходной длительностью — менять нечего.');
    if (target > src * MAX_MUL) return t('err_too_long', 'Больше :max не получится — повторы станут слышны.').replace(':max', fmt(src * MAX_MUL));
    if (target < src * MIN_MUL) return t('err_too_short', 'Короче :min не получится — от песни мало что останется.').replace(':min', fmt(src * MIN_MUL));
    return '';
}

function showMsg(text, kind = 'warn') {
    const el = $('es-msg');
    if (!el) return;
    el.hidden = !text;
    el.className = 'tc-msg es-msg ' + kind;
    el.textContent = text || '';
}

function updateSummary() {
    if (!S.buffer) return;
    const src = S.buffer.duration;
    if (S.mode === 'song') {
        const el = $('es-summary');
        const target = readTarget();
        if (!isFinite(target) || Math.abs(target - src) < 1) { put(el, ''); return; }
        put(el, t(target > src ? 'sum_song' : 'sum_song_short', ':delta · :mul')
            .replace(':delta', fmtDelta(target - src)).replace(':mul', fmtMul(target / src)));
    } else {
        const sum = $('es-region-sum');
        const work = $('es-work');
        if (work) work.toggleAttribute('data-region', !!S.region);
        // Без участка запускать нечего: кнопка выключена и говорит почему —
        // 64 сессии за 30 дней жали её вхолостую и получали тост «выделите».
        const run = $('es-stretch');
        if (run) {
            run.disabled = !S.region || S.busy;
            put(run, S.region ? t('run_region', 'Протянуть участок') : t('run_region_pick', 'Сначала выделите участок'));
        }
        if (S.regionAct === 'repeat') { paintRepeatSummary(); return; }
        if (!S.region) { put(sum, t('sum_pick', 'Выделите участок на волне оригинала: проведите по ней.')); return; }
        const len = S.region.end - S.region.start;
        const f = parseFloat($('es-factor').value);
        const rise = parseFloat($('es-rise').value);
        put(sum, t('sum_region', 'Участок :a–:b (:len) станет :newlen. Песня: :from → :to.')
            .replace(':a', LRI + fmt(S.region.start)).replace(':b', fmt(S.region.end) + PDI)
            .replace(':len', num(len)).replace(':newlen', num(len * f))
            .replace(':from', fmt(src)).replace(':to', fmt(src + len * (f - 1)))
            + (rise > 0 ? ' ' + t('sum_rise', 'В конце — подъём на :n полутона.').replace(':n', String(rise)) : ''));
    }
}

/* ── План и сборка («Длина песни») ────────────────────────────────────────── */

function schedulePlan(via, delay = 260) {
    S.planVia = via || S.planVia;
    clearTimeout(S.planTimer);
    S.planTimer = setTimeout(() => { buildPlan(); }, delay);
}

function ensureAnalysis() {
    if (S.analysis) return Promise.resolve(S.analysis);
    if (!S.analyzing) {
        const file = S.file;
        const chans = channelsOf(S.buffer);
        stage('analyze');
        setStatus(t('analyzing', 'Слушаю песню: ищу доли и такты…'), 0);
        S.analyzing = analyzeAsync(chans, S.buffer.sampleRate, {
            onProgress: (p) => { if (S.file === file) setStatus(t('analyzing', 'Слушаю песню: ищу доли и такты…'), p * 0.95); },
        }).then((a) => {
            if (S.file !== file) return null;
            S.analysis = a;
            setStatus('');
            paintFacts();
            ae('analyzed', { bpm: Math.round(a.bpm), bars: Math.round(a.beatTimes.length / a.beatsPerBar), srcSec: Math.round(S.buffer.duration) });
            return a;
        }, (err) => {
            if (S.file === file) { S.analyzing = null; setStatus(''); }
            throw err;
        });
    }
    return S.analyzing;
}

function paintFacts() {
    const a = S.analysis;
    const el = $('es-facts');
    if (!el || !a) return;
    const bars = Math.max(1, Math.round(a.beatTimes.length / a.beatsPerBar));
    put(el, Math.round(a.bpm) + ' BPM · ' + plural('n_bars', bars));
    el.hidden = false;
}

async function buildPlan() {
    if (S.mode !== 'song' || !S.buffer) return;
    const file = S.file;
    const target = readTarget();
    const problem = targetProblem(target);
    updateSummary();
    if (problem) { showMsg(problem); return; }
    showMsg(target > S.buffer.duration * CAUTION_MUL ? t('warn_many_repeats', 'Материала мало: части песни будут повторяться по нескольку раз — послушайте швы.') : '', 'warn');

    let a;
    try { a = await ensureAnalysis(); }
    catch (err) {
        if (S.file !== file) return;
        if (err && err.code === 'NO_BEAT_GRID') {
            // Тактов нет вовсе — для человека это тот же ответ, что «негде склеить».
            showMsg(t('err_no_plan', 'В этом треке не нашлось похожих мест для незаметной склейки.'), 'bad');
            try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'no_plan', slug: SLUG, reason: 'no_beat_grid' }); } catch (_) {}
        } else window.__handleError(err, SLUG + '_analyze', { say: t('err_analyze', 'Не получилось разобрать этот файл.') });
        ae('run_failed', { mode: 'song', err: errLabel(err), stage: 'analyze' });
        return;
    }
    if (!a || S.file !== file || S.mode !== 'song') return;
    if (readTarget() !== target) return;   // пока анализировали, цель поменяли — соберёт следующий вызов

    stage('plan');
    const t0 = performance.now();
    const pv = planVariants(a, target, { exclude: S.excluded.size ? S.excluded : null });
    if (!pv.ok) {
        S.song = null; S.variants = null;
        transport.replace('out', null);
        paintResult();
        showMsg(t(pv.reason === 'too_small' ? 'err_same' : 'err_no_plan', 'В этом треке не нашлось похожих мест для незаметной склейки.'), 'bad');
        try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'no_plan', slug: SLUG, reason: pv.reason || 'none' }); } catch (_) {}
        ae('run_failed', { mode: 'song', err: 'no_plan:' + (pv.reason || ''), user: 0 });
        return;
    }
    ae('run_start', { mode: 'song', via: S.planVia });
    S.target = target;
    S.variants = pv.variants;
    S.descs = describeVariants(pv.variants, a.beatsPerBar, a.beatTimes);
    renderVariant(0);
    stage('preview');
    ae('result_shown', {
        mode: 'song', via: S.planVia,
        variants: S.variants.length, excluded: S.excluded.size,
        seams: S.song ? S.song.seamTimes.length : 0,
        outSec: Math.round(target), srcSec: Math.round(S.buffer.duration),
        addedPct: Math.round((target / S.buffer.duration - 1) * 100),
        land: S.song && S.song.landed ? S.song.landed.how : null,
        planMs: Math.round(performance.now() - t0),
    });
}

/** Собрать вариант сразу в каналы AudioBuffer плеера — без второй копии. */
function renderVariant(i) {
    const v = S.variants && S.variants[i];
    if (!v) return;
    const a = S.analysis;
    const sr = S.buffer.sampleRate;
    const ranges = planToRanges(a, v.segments);
    // Буфер — сразу длиной цели, если план вышел короче: недостающее — тишина
    // в конце, ровно как в скачанном файле, и копировать трек ради неё не нужно.
    const want = Math.round(S.target * sr);
    let audio = null, total = 0;
    const r = renderSegments(channelsOf(S.buffer), sr, ranges, {
        crossfadeMs: 30,
        alloc: (n, nch) => { total = n; audio = makeAudioBuffer(nch, Math.max(n, want), sr); return channelsOf(audio); },
    });
    if (!audio || audio.length < r.channels[0].length) {
        total = r.channels[0].length;
        audio = makeAudioBuffer(r.channels.length, Math.max(total, want), sr);
        r.channels.forEach((c, k) => audio.copyToChannel(c, k));
    }
    const channels = channelsOf(audio);
    S.variantIndex = i;
    S.song = {
        channels, sampleRate: sr, duration: audio.duration,
        seamTimes: r.seamTimes, variant: v, ranges,
        map: rangesToMap(ranges),
        landed: total < want - 1 ? { how: 'pad', sec: (want - total) / sr } : landKind(channels, sr, S.target),
        audio,
    };
    S.activeSeam = -1;
    transport.replace('out', audio);
    paintResult();
}

/** Что прозвучит дважды и что выпадет — полосами на оригинале. */
function sourceMarks(res, srcDur) {
    if (!res || !res.map) return [];
    const ev = [];
    for (const m of res.map) { ev.push([m.srcStart, 1]); ev.push([m.srcEnd, -1]); }
    ev.sort((x, y) => x[0] - y[0] || x[1] - y[1]);
    const out = [];
    let depth = 0, last = 0;
    const push = (a, b, kind) => { if (b - a > 0.05) out.push({ start: a, end: b, kind }); };
    for (const [x, d] of ev) {
        if (x > last) {
            if (depth === 0) push(last, x, 'cut');
            else if (depth >= 2) push(last, x, 'twice');
        }
        depth += d; last = x;
    }
    if (last < srcDur) push(last, srcDur, 'cut');
    return out.filter((m) => !(m.kind === 'cut' && m.end - m.start < 0.3));
}

function paintResult() {
    const res = current();
    const srcDur = S.buffer ? S.buffer.duration : 0;
    const work = $('es-work');
    if (work) work.toggleAttribute('data-has-result', !!res);
    // «Результат» в переключателе — только когда есть что слушать.
    const abOut = document.querySelector('[data-es-view="out"]');
    if (abOut) abOut.disabled = !res;

    const marks = S.mode === 'song' || (S.regionAct === 'repeat' && res) ? sourceMarks(res, srcDur) : [];
    if (waveIn) waveIn.set({ marks });
    const empty = $('es-out-empty');
    if (empty) {
        put(empty, S.mode !== 'region' ? t('out_empty', 'Здесь появится новая версия')
            : S.regionAct === 'repeat' ? t('out_empty_repeat', 'Выделите участок на оригинале — повтор появится здесь сразу')
                : t('out_empty_region', 'Выделите участок на оригинале и нажмите «Протянуть участок»'));
    }
    if (res) {
        waveOut.setData(res.channels, res.duration, srcDur);
        waveOut.set({ map: res.map, seams: res.seamTimes, activeSeam: S.activeSeam });
        if (empty) empty.hidden = true;
    } else {
        if (empty) empty.hidden = false;
        if (waveOut) waveOut.clear();
        if (S.view === 'out') setView('src');
    }

    // Подпись дорожки: длина, на сколько изменилась, сколько швов.
    let meta = '';
    if (res) {
        const len = S.mode === 'song' ? S.target : res.duration;
        meta = fmt(len) + ' · ' + fmtDelta(len - srcDur);
        if (S.mode === 'song' || S.regionAct === 'repeat') meta += ' · ' + plural('n_seams', res.seamTimes.length);
    }
    put($('es-out-meta'), meta);
    const legend = $('es-legend');
    if (legend) {
        legend.hidden = !marks.length;
        const hasTwice = marks.some((m) => m.kind === 'twice'), hasCut = marks.some((m) => m.kind === 'cut');
        $('es-legend-twice').hidden = !hasTwice;
        $('es-legend-cut').hidden = !hasCut;
    }

    paintVariants();
    paintSeams();
    paintExport();
    if (res && S.view === 'src' && !transport.playing) setView('out');
    paintPlayhead(transport.position(), transport.which);
    syncBusy();
}

function paintVariants() {
    const box = $('es-variants');
    if (!box) return;
    box.innerHTML = '';
    const many = S.mode === 'song' && S.variants && S.variants.length > 1 && S.song && S.descs;
    box.hidden = !many;
    if (!many) return;
    const light = isLight();
    const srcDur = S.buffer.duration;
    S.variants.forEach((v, i) => {
        const d = S.descs[i];
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'es-variant' + (i === S.variantIndex ? ' on' : '');
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-checked', i === S.variantIndex ? 'true' : 'false');
        const name = document.createElement('span');
        name.className = 'es-v-name';
        // Диапазон времени — изолятом «слева направо»: в арабском «1:09–3:12»
        // иначе читается как «3:12–1:09» (снимок /ar, 16.10.2026).
        name.textContent = d.trait === 'piece' && d.piece
            ? t(d.piece.kind === 'cut' ? 'v_piece_cut' : 'v_piece_repeat', 'Повтор :a–:b').replace(':a', LRI + fmt(d.piece.start)).replace(':b', fmt(d.piece.end) + PDI)
            : t('v_' + d.trait, d.trait);
        if (d.recommended) {
            const rec = document.createElement('em');
            rec.textContent = t('v_recommended', 'лучший');
            name.appendChild(rec);
        }
        const sub = document.createElement('span');
        sub.className = 'es-v-sub';
        const q = document.createElement('b');
        q.setAttribute('data-q', d.quality);
        q.textContent = t('q_' + d.quality, d.quality);
        sub.append(document.createTextNode(plural('n_seams', d.seams) + ' · '), q);
        // Мини-карта: цвета мест оригинала в порядке звучания этого варианта.
        const strip = document.createElement('i');
        strip.className = 'es-v-map';
        strip.setAttribute('aria-hidden', 'true');
        const ranges = planToRanges(S.analysis, v.segments);
        const total = ranges.reduce((s, r) => s + (r.end - r.start), 0) || 1;
        const stops = [];
        let acc = 0;
        for (const r of ranges) {
            const c0 = placeColor(r.start / srcDur, light), c1 = placeColor(r.end / srcDur, light);
            stops.push(`rgb(${c0}) ${(acc / total * 100).toFixed(2)}%`);
            acc += r.end - r.start;
            stops.push(`rgb(${c1}) ${(acc / total * 100).toFixed(2)}%`);
        }
        strip.style.backgroundImage = `linear-gradient(90deg, ${stops.join(',')})`;
        b.append(name, sub, strip);
        b.addEventListener('click', () => pickVariant(i));
        box.appendChild(b);
    });
}

function paintSeams() {
    const box = $('es-seams');
    if (!box) return;
    box.innerHTML = '';
    const res = current();
    const seams = res ? res.seamTimes : [];
    box.hidden = !seams.length;
    if (!seams.length) return;
    const canDrop = S.mode === 'song' && res.variant && res.variant.jumps && res.variant.jumps.length;
    const lab = document.createElement('span');
    lab.className = 'es-seams-l';
    lab.textContent = t('seams_label', 'Швы:');
    box.appendChild(lab);
    seams.forEach((sec, i) => {
        const row = document.createElement('span');
        row.className = 'es-seam' + (i === S.activeSeam ? ' is-active' : '');
        row.setAttribute('data-i', String(i));
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'es-seam-b';
        b.innerHTML = '<svg viewBox="0 0 24 24" width="11" height="11" aria-hidden="true"><path d="M7 5v14l12-7z" fill="currentColor"/></svg>';
        b.append(document.createTextNode(fmt(sec)));
        b.setAttribute('aria-label', t('seam_play', 'Послушать шов :n').replace(':n', String(i + 1)) + ' (' + fmt(sec) + ')');
        b.title = t('seam_play', 'Послушать шов :n').replace(':n', String(i + 1));
        b.addEventListener('click', () => auditionSeam(i));
        row.appendChild(b);
        // Убрать шов можно только в автоматическом режиме: в ручном его задал сам человек.
        if (canDrop) {
            const x = document.createElement('button');
            x.type = 'button';
            x.className = 'es-seam-x';
            x.title = t('seam_drop', 'Убрать этот шов и пересобрать');
            x.setAttribute('aria-label', x.title + ' (' + fmt(sec) + ')');
            x.innerHTML = '<svg viewBox="0 0 24 24" width="11" height="11" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>';
            x.addEventListener('click', () => dropSeam(i));
            row.appendChild(x);
        }
        box.appendChild(row);
    });
}

function paintExport() {
    const res = current();
    const fmtName = S.format === 'wav' ? 'WAV' : 'MP3';
    put($('es-dl-label'), t('dl', 'Скачать :fmt').replace(':fmt', fmtName));
    const dl = $('es-dl');
    if (dl) dl.disabled = !res || S.busy;
    // Как будет добрана точная длина — говорим, если это хоть в теории слышно.
    let note = '';
    if (res && S.mode === 'song') {
        const land = res.landed || {};
        if (land.how !== 'skip') {
            note = t('land_exact', 'Файл будет ровно :dur').replace(':dur', fmtTarget(S.target));
            if (land.how === 'pad' && land.sec >= 0.05) note += ' — ' + t('landed_pad', 'в конце :s с тишины').replace(':s', num(land.sec));
            else if (land.how === 'stretch') note += ' — ' + t('landed_stretch', 'темп +:pct %, на слух незаметно').replace(':pct', num((land.rate - 1) * 100));
            note += '.';
        }
    } else if (res && S.regionAct === 'repeat' && S.repeat) {
        note = t('repeat_done', 'Участок звучит :times подряд, песня длится :dur.')
            .replace(':times', plural('n_times', S.repeat.times)).replace(':dur', fmt(res.duration));
    } else if (res) {
        note = t('region_done', 'Участок протянут, песня длится :dur.').replace(':dur', fmt(res.duration));
    }
    const el = $('es-land');
    put(el, note);
    if (el) el.hidden = !note;
}

function pickVariant(i) {
    if (S.busy || !S.variants || !S.variants[i]) return;
    // Повторное нажатие на выбранный вариант — «послушать его»: за 30 дней до
    // 22.10.2026 160 таких щелчков в 66 сессиях уходили в пустоту (silent_click).
    // Отличает вариант его шов — его и играем, окном 2 + 2 с.
    if (i === S.variantIndex) {
        if (S.song && S.song.seamTimes.length) { ae('variant_replay', { i }); auditionSeam(0); }
        return;
    }
    renderVariant(i);
    ae('variant_picked', { i, trait: S.descs && S.descs[i] ? S.descs[i].trait : '', stars: S.variants[i].stars });
}

/** Шов i → прыжок плана, который его открывает. Возврат с count=2 даёт два
 *  шва одного прыжка, поэтому считаем по порядку звучания, а не по индексу. */
function jumpOfSeam(res, i) {
    const v = res && res.variant;
    if (!v || !v.jumps || !v.jumps.length) return null;
    const segs = v.segments || [];
    // Шов k — стык сегментов k и k+1 внутри сетки (planToRanges может добавить
    // голову до сетки, она швов не даёт: contiguous).
    const a = segs[i], b = segs[i + 1];
    if (a && b) {
        const hit = v.jumps.find((j) => j.from === a.toBeat && j.to === b.fromBeat);
        if (hit) return hit;
    }
    return v.jumps[Math.min(i, v.jumps.length - 1)];
}

/** Убрать конкретный шов и пересобрать: точка уходит в запрет. */
function dropSeam(i) {
    if (S.busy || !S.song) return;
    const j = jumpOfSeam(S.song, i);
    if (!j) return;
    S.excluded.add(j.from + ':' + j.to);
    ae('seam_dropped', { total: S.excluded.size });
    schedulePlan('seam_drop', 0);
}

async function auditionSeam(i) {
    const res = current();
    if (!res) return;
    const sec = res.seamTimes[i];
    S.activeSeam = i;
    if (waveOut) waveOut.set({ activeSeam: i });
    document.querySelectorAll('.es-seam').forEach((el) => el.classList.toggle('is-active', +el.getAttribute('data-i') === i));
    setView('out');
    ae('seam_audition', { i });
    // Окно вокруг шва: две секунды до и две после, потом тишина — так слышно
    // сам переход, а не следующие полминуты песни.
    await transport.play('out', Math.max(0, sec - 2), { until: Math.min(res.duration, sec + 2.2) });
    syncPlayButton(transport.playing);
}

/* ── «Повторить участок» ─────────────────────────────────────────────────────
 * Обращение 22.10.2026 (гость, готовит выступление): «в мелодии нужно продлить
 * фрагмент выхода 2 раза… не удаётся сделать шов чуть дальше, чем начинается
 * мелодия». План «Длины песни» вступление не трогает (защита краёв), а режим
 * участка умел только растягивать — то есть замедлял музыку. Теперь участок
 * звучит N раз подряд, остальная песня на месте; края встают на первую долю
 * такта и двигаются шагом в такт. Сборка — те же отрезки исходника и тот же
 * renderSegments, что у плана: миллисекунды, поэтому результат живой, как у
 * «Длины песни», без кнопки «Применить». Замеры — у repeatRegion в plan.js. */

let repeatSeq = 0;

function readTimes() {
    const v = parseInt($('es-times').value, 10);
    return Math.max(2, Math.min(8, isFinite(v) ? v : 2));
}

async function buildRepeat(via) {
    if (S.mode !== 'region' || S.regionAct !== 'repeat' || !S.buffer) return;
    const my = ++repeatSeq;
    if (!S.region) {
        S.repeat = S.repeatResult = S.repeatNear = null;
        transport.replace('out', null);
        paintResult();
        updateSummary();
        return;
    }
    const file = S.file;
    const sel = { ...S.region };
    // Без сетки долей (речь, шум, запись без ритма) planRepeat оставит участок
    // ровно как выделен: привязка к случайной «сетке» сдвинула бы края зря.
    let a = null;
    try { a = await ensureAnalysis(); } catch (_) { a = null; }
    if (S.file !== file || my !== repeatSeq || S.mode !== 'region' || S.regionAct !== 'repeat') return;
    const r = planRepeat(a, S.buffer.duration, sel.start, sel.end, readTimes());
    if (!r.ok) { S.repeat = S.repeatResult = S.repeatNear = null; paintResult(); updateSummary(); return; }
    S.repeat = r;
    S.repeatExact = r.exact;
    // Волна показывает то, что правда повторится, — края уже на тактах.
    S.region = { start: r.start, end: r.end };
    if (waveIn) waveIn.set({ region: { ...S.region } });
    S.repeatNear = r.suggestion;
    renderRepeat(r);
    stage('preview');
    ae('result_shown', {
        mode: 'region', act: 'repeat', via: via || 'select', times: r.times, bars: r.bars,
        len: +(r.end - r.start).toFixed(1), stars: r.stars, exact: S.repeatExact ? 1 : 0,
        near: S.repeatNear ? S.repeatNear.stars : 0,
        outSec: Math.round(S.repeatResult.duration), srcSec: Math.round(S.buffer.duration),
    });
}

function renderRepeat(r) {
    const sr = S.buffer.sampleRate;
    let audio = null;
    const res = renderSegments(channelsOf(S.buffer), sr, r.ranges, {
        crossfadeMs: 30,
        alloc: (n, nch) => { audio = makeAudioBuffer(nch, n, sr); return channelsOf(audio); },
    });
    if (!audio || audio.length < res.channels[0].length) {
        audio = makeAudioBuffer(res.channels.length, res.channels[0].length, sr);
        res.channels.forEach((c, k) => audio.copyToChannel(c, k));
    }
    S.repeatResult = {
        channels: channelsOf(audio), sampleRate: sr, duration: audio.duration,
        seamTimes: res.seamTimes, map: rangesToMap(r.ranges), audio,
    };
    S.activeSeam = -1;
    transport.replace('out', audio);
    paintResult();
    updateSummary();
}

/** Край участка на такт раньше или позже. */
function nudgeEdge(edge, d) {
    const r = S.repeat, a = S.analysis;
    if (S.busy || !r || S.repeatExact || !a) return;
    const bpb = a.beatsPerBar;
    let i = r.toBeat, j = r.fromBeat;
    if (edge === 'start') i += d * bpb; else j += d * bpb;
    if (i < 0 || j > a.beatTimes.length - 1 || j - i < bpb) return;
    S.region = { start: a.beatTimes[i], end: a.beatTimes[j] };
    ae('repeat_nudge', { edge, d });
    buildRepeat('nudge');
}

function paintRepeatSummary() {
    const sum = $('es-region-sum');
    const r = S.repeat;
    const edges = $('es-edges'), note = $('es-seam-note'), sug = $('es-suggest');
    if (edges) edges.hidden = !r || S.repeatExact;
    if (!r || !S.buffer) {
        put(sum, S.region ? '' : t('sum_pick', 'Выделите участок на волне оригинала: проведите по ней.'));
        if (note) note.hidden = true;
        if (sug) sug.hidden = true;
        return;
    }
    const src = S.buffer.duration;
    const len = r.end - r.start;
    put(sum, t('sum_repeat', 'Участок :a–:b (:len) прозвучит :times подряд. Песня: :from → :to.')
        .replace(':a', LRI + fmtTarget(r.start)).replace(':b', fmtTarget(r.end) + PDI)
        .replace(':len', r.bars ? plural('n_bars', r.bars) : t('len_sec', ':n с').replace(':n', num(len)))
        .replace(':times', plural('n_times', r.times))
        .replace(':from', fmt(src)).replace(':to', fmt(src + len * (r.times - 1))));
    put($('es-edge-start'), fmtTarget(r.start));
    put($('es-edge-end'), fmtTarget(r.end));
    if (note) {
        note.textContent = '';
        if (S.repeatExact) note.textContent = t('repeat_exact', 'Долей в этой записи не нашлось — шов встанет ровно там, где вы выделили.');
        else if (r.stars != null) {
            const q = seamQuality(r.stars);
            const b = document.createElement('b');
            b.setAttribute('data-q', q);
            b.textContent = t('q1_' + q, q);
            note.append(document.createTextNode(t('seam_q_label', 'Шов') + ': '), b);
        }
        note.hidden = !note.textContent;
    }
    if (sug) {
        const n = S.repeatNear;
        sug.hidden = !n;
        if (n) {
            put(sug, t('suggest', 'Ровнее: :a–:b (:len)')
                .replace(':a', LRI + fmtTarget(n.start)).replace(':b', fmtTarget(n.end) + PDI)
                .replace(':len', plural('n_bars', n.bars)));
        }
    }
    syncBusy();
}

function setAct(act, via) {
    if (S.busy) return;
    S.regionAct = act === 'stretch' ? 'stretch' : 'repeat';
    document.querySelectorAll('[data-es-act]').forEach((b) => {
        const on = b.getAttribute('data-es-act') === S.regionAct;
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    $('es-act-repeat').hidden = S.regionAct !== 'repeat';
    $('es-act-stretch').hidden = S.regionAct !== 'stretch';
    const cur = current();
    transport.replace('out', cur ? cur.audio : null);
    if (via) ae('act_switch', { act: S.regionAct, via });
    updateSummary();
    if (S.buffer) paintResult();
    if (S.mode === 'region' && S.regionAct === 'repeat' && S.region && !S.repeatResult) buildRepeat('act');
}

/* ── «Растянуть участок» ─────────────────────────────────────────────────── */

async function runRegion() {
    if (S.busy || !S.buffer) return;
    if (!S.region) { showMsg(t('err_no_region', 'Сначала выделите участок на волне оригинала.')); return; }
    showMsg('');
    transport.pause();
    setBusy(true);
    ae('run_start', { mode: 'region' });
    try {
        await ensurePipeline();
        const factor = parseFloat($('es-factor').value);
        const rise = parseFloat($('es-rise').value);
        stage('stretch');
        setStatus(t('stretching', 'Протягиваю участок…'), 0.05);
        const sr = S.buffer.sampleRate;
        const region = { ...S.region };
        const r = await stretchRegion(channelsOf(S.buffer), sr, {
            start: region.start, end: region.end, factor, riseSemitones: rise,
            onProgress: (p) => setStatus(t('stretching', 'Протягиваю участок…'), 0.05 + p * 0.9),
        });
        const audio = makeAudioBuffer(r.channels.length, r.channels[0].length, sr);
        r.channels.forEach((c, k) => audio.copyToChannel(c, k));
        const srcDur = S.buffer.duration;
        S.regionResult = {
            channels: r.channels, sampleRate: sr, duration: r.duration, seamTimes: r.seamTimes,
            map: [
                { outStart: 0, outEnd: r.regionStart, srcStart: 0, srcEnd: region.start },
                { outStart: r.regionStart, outEnd: r.regionEnd, srcStart: region.start, srcEnd: region.end },
                { outStart: r.regionEnd, outEnd: r.duration, srcStart: region.end, srcEnd: srcDur },
            ].filter((m) => m.outEnd - m.outStart > 0.001),
            audio,
        };
        transport.replace('out', audio);
        setStatus('');
        paintResult();
        setView('out');
        stage('preview');
        ae('result_shown', { mode: 'region', outSec: Math.round(r.duration), srcSec: Math.round(srcDur), factor, rise });
    } catch (err) {
        setStatus('');
        window.__handleError(err, SLUG + '_stretch', { say: t('err_stretch', 'Не получилось протянуть участок. Попробуйте участок покороче.') });
        ae('run_failed', { mode: 'region', err: errLabel(err) });
    } finally {
        setBusy(false);
    }
}

/* ── Скачивание ────────────────────────────────────────────────────────────
 * Единица счётчика — ФАЙЛ (повтор того же файла в тот же день бесплатен), и
 * тратится она здесь, на выдаче: подбор длины, варианты и прослушивание не
 * стоят ничего. */
async function download() {
    const res = current();
    if (!res || S.busy) return;
    const limit = window.__toolLimit;
    const token = limit ? limit.begin(SLUG, limit.fileKey(S.file)) : { commit() {}, abort() {} };
    if (!token) return;
    const format = S.format;
    const base = (S.file && S.file.name ? S.file.name.replace(/\.[^.]+$/, '') : 'track');
    const pr = panel('es-export-progress');
    const ctrl = (typeof AbortController === 'function') ? new AbortController() : null;
    const t0 = Date.now();
    setBusy(true);
    transport.pause();
    try {
        if (pr) {
            pr.onCancel(ctrl ? () => ctrl.abort() : null);
            // indeterminate: первые проценты MP3 приходят только через ~10 с.
            pr.start(t('exporting', 'Готовлю файл…'), { indeterminate: true });
        }
        await ensurePipeline();
        // Доводка длины ускорением — отдельный проход вокодера (секунды-десятки
        // секунд): его доля полосы идёт первой, кодирование — следом, и процент
        // не стоит на нуле, пока работает первый проход.
        const stretching = S.mode === 'song' && res.landed && res.landed.how === 'stretch';
        const share = stretching ? (format === 'mp3' ? 0.35 : 0.9) : 0;
        let chans = res.channels;
        if (S.mode === 'song') {
            chans = await landExactly(res, S.target, {
                signal: ctrl ? ctrl.signal : null,
                onProgress: stretching && pr ? (q) => pr.set(Math.max(0, Math.min(1, q)) * share) : null,
            });
        }
        let blob, ext;
        if (format === 'mp3') {
            if (!window.AudioPipeline || !window.AudioPipeline.encodeMP3) throw new Error('MP3 encoder unavailable');
            blob = await window.AudioPipeline.encodeMP3(makeBuffer(chans, res.sampleRate), {
                // Битрейт — из общего переключателя (<x-mp3-quality id="esQuality">).
                bitrate: (window.__mp3Quality ? window.__mp3Quality.value('esQuality', 320) : 320), slug: SLUG,
                signal: ctrl ? ctrl.signal : null,
                onProgress: (p) => { if (pr) pr.set(share + p * (1 - share)); },
            });
            ext = 'mp3';
        } else {
            const enc = wavEncoder();
            if (!enc) throw new Error('WAV encoder unavailable');
            blob = enc(makeBuffer(chans, res.sampleRate));
            ext = 'wav';
        }
        if (pr) pr.finishing();
        const shorter = S.mode === 'song' && S.target < S.buffer.duration;
        const name = base + '-' + (shorter ? t('suffix_short', 'сокращённая') : t('suffix', 'продлённая')) + '.' + ext;
        const ok = await window.__downloadFile(blob, name);
        if (ok === false) { token.abort(); return; }
        token.commit();
        stage('complete');
        try {
            window.__aeToolComplete && window.__aeToolComplete({
                slug: SLUG, format: ext, mode: S.mode,
                act: S.mode === 'region' ? S.regionAct : null,
                times: S.mode === 'region' && S.regionAct === 'repeat' && S.repeat ? S.repeat.times : null,
                srcSec: Math.round(S.buffer.duration),
                outSec: Math.round(chans[0].length / res.sampleRate),
                seams: res.seamTimes.length,
                variant: S.mode === 'song' ? S.variantIndex : null,
                trait: S.mode === 'song' && S.descs && S.descs[S.variantIndex] ? S.descs[S.variantIndex].trait : null,
                exportMs: Date.now() - t0,
            });
        } catch (_) {}
    } catch (err) {
        token.abort();
        const cancelled = window.__isUserCancel ? window.__isUserCancel(err, ctrl ? ctrl.signal : null) : (err && err.name === 'AbortError');
        if (cancelled) {
            ae('export_cancelled', { format, outSec: Math.round(res.duration), waitedMs: Date.now() - t0 });
            window.__toast(t('cancelled', 'Обработка отменена.'), 'info');
        } else {
            ae('export_failed', { format, outSec: Math.round(res.duration), err: errLabel(err) });
            window.__handleError(err, SLUG + '_export', { say: t('err_export', 'Не получилось подготовить файл. Попробуйте WAV.') });
        }
    } finally {
        if (pr) pr.hide();
        setBusy(false);
    }
}

function setFormat(f, via) {
    S.format = f === 'wav' ? 'wav' : 'mp3';
    document.querySelectorAll('[data-es-fmt]').forEach((b) => {
        const on = b.getAttribute('data-es-fmt') === S.format;
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    const q = $('esQuality');
    if (q) q.hidden = S.format !== 'mp3';
    paintExport();
    if (via) {
        try { localStorage.setItem('es_format', S.format); } catch (_) {}
        ae('format_pick', { format: S.format });
    }
}

/* ── Исходник ────────────────────────────────────────────────────────────── */

function defaultTarget(d) {
    const fromLink = linkTarget();
    if (fromLink) return fromLink;
    // ×2, округлённое до 5 с — понятная отправная точка, с которой сразу видно,
    // как работает план; дальше человек двигает длину шагами или подсказками.
    return Math.max(5, Math.round((d * 2) / 5) * 5);
}

async function onFile(file) {
    transport.stop();
    clearTimeout(S.planTimer);
    S.file = file;
    S.buffer = S.analysis = S.analyzing = null;
    S.song = S.regionResult = S.variants = S.descs = S.region = null;
    S.repeat = S.repeatResult = S.repeatNear = null;
    S.repeatExact = false;
    S.excluded = new Set();
    S.activeSeam = -1;
    $('es-work').hidden = false;
    const sample = $('es-sample');
    if (sample) sample.hidden = true;
    const facts = $('es-facts');
    if (facts) { put(facts, ''); facts.hidden = true; }
    showMsg('');
    setStatus(t('decoding', 'Читаю файл…'), 0.05);
    ae('file_loaded', { sizeMB: Math.round(file.size / 1048576) });
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const buf = await window.__decodeAudio(ctx, file);
        try { ctx.close(); } catch (_) {}
        if (S.file !== file) return;
        S.buffer = buf;
        setStatus('');

        waveIn.setData(channelsOf(buf), buf.duration, buf.duration);
        waveIn.set({ region: null, marks: [], map: null });
        transport.set('src', buf);
        transport.set('out', null);
        transport.offset = 0;
        put($('es-src-meta'), fmt(buf.duration));

        const target = defaultTarget(buf.duration);
        $('es-target').value = fmtTarget(target);
        if (linkTarget()) { setMode('song'); ae('target_from_link', { sec: Math.round(target), srcSec: Math.round(buf.duration) }); }
        renderPresets();
        updateSummary();
        paintResult();
        if (applyRepeatHint()) return;
        if (S.mode === 'song') schedulePlan('auto', 0);
        else ensureAnalysis().catch(() => {});
    } catch (err) {
        setStatus('');
        window.__handleError(err, SLUG + '_decode', { say: t('err_decode', 'Не получилось прочитать этот файл.') });
    }
}

function renderPresets() {
    const box = $('es-presets');
    if (!box || !S.buffer) return;
    box.innerHTML = '';
    const d = S.buffer.duration;
    // Чаще всего песню удлиняют чуть-чуть: в телеметрии 30 дней больше всего
    // результатов до ×1,6. Поэтому сначала шаги «+30 с» и «+1 мин», потом кратные.
    const opts = [
        { label: t('chip_plus30', '+30 с'), sec: d + 30, via: 'plus30' },
        { label: t('chip_plus60', '+1 мин'), sec: d + 60, via: 'plus60' },
        { label: fmtMul(1.5), sec: d * 1.5, via: 'x1.5' },
        { label: '×2', sec: d * 2, via: 'x2' },
        { label: '×3', sec: d * 3, via: 'x3' },
    ].filter((o) => o.sec > d + 1 && o.sec <= d * MAX_MUL);
    for (const o of opts) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'es-chip';
        b.textContent = o.label;
        b.addEventListener('click', () => setTarget(Math.round(o.sec), o.via));
        box.appendChild(b);
    }
    syncBusy();
}

function setTarget(sec, via) {
    if (S.busy || !S.buffer) return;
    $('es-target').value = fmtTarget(sec);
    if (S.mode !== 'song') setMode('song');
    updateSummary();
    ae('target_set', { via, sec: Math.round(sec), srcSec: Math.round(S.buffer.duration) });
    schedulePlan(via, 0);
}

/** Длина из другого файла (видео, озвучка): читается только заголовок. */
async function lengthFromFile(file) {
    if (!file || !S.buffer) return;
    let sec = 0;
    try {
        const ADR = window.AudioDecoderRobust;
        const info = ADR && ADR.probeDuration ? await ADR.probeDuration(file) : null;
        sec = info && info.durSec > 0 ? info.durSec : 0;
    } catch (_) { sec = 0; }
    if (!(sec > 0)) {
        window.__toast(t('video_len_fail', 'Не удалось узнать длительность этого файла.'), 'warning');
        ae('target_from_file', { ok: 0 });
        return;
    }
    // Видео длится дробное число секунд, а музыка должна кончиться ровно с
    // последним кадром — поэтому цель берём с десятой, а не округляем.
    const exact = Math.round(sec * 10) / 10;
    window.__toast(t('video_len_set', 'Длительность взята из «:name»: :dur').replace(':name', file.name).replace(':dur', fmtTarget(exact)), 'info');
    ae('target_from_file', { ok: 1, sec: Math.round(exact) });
    setTarget(exact, 'file');
}

/** Пример из корпуса стилей — пройти весь путь без своего файла. */
async function loadSample() {
    const btn = $('es-sample-btn');
    if (!btn || btn.disabled) return;
    btn.disabled = true;
    put($('es-sample-label'), t('sample_loading', 'Загружаю пример…'));
    ae('sample_used', {});
    try {
        const r = await fetch(btn.getAttribute('data-src'));
        if (!r.ok) throw new Error('sample HTTP ' + r.status);
        const blob = await r.blob();
        const file = new File([blob], t('sample_name', 'пример.mp3'), { type: 'audio/mpeg' });
        if (!(window.ToolUploader && window.ToolUploader.inject('es-drop', file, 'sample_file'))) throw new Error('sample: uploader not ready');
    } catch (err) {
        window.__handleError(err, SLUG + '_sample', { say: t('err_sample', 'Не получилось загрузить пример. Попробуйте ещё раз.') });
    } finally {
        btn.disabled = false;
        put($('es-sample-label'), t('sample_btn', 'Попробовать на примере'));
    }
}

/* ── Режимы ──────────────────────────────────────────────────────────────── */

function setMode(mode, via) {
    if (S.busy) return;
    S.mode = mode;
    document.querySelectorAll('[data-es-mode]').forEach((b) => {
        const on = b.getAttribute('data-es-mode') === mode;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    $('es-mode-song').hidden = mode !== 'song';
    $('es-mode-region').hidden = mode !== 'region';
    const work = $('es-work');
    if (work) work.setAttribute('data-mode', mode);
    showMsg('');
    if (waveIn) waveIn.set({ selectable: mode === 'region', region: mode === 'region' ? S.region : null });
    const cur = current();
    transport.replace('out', cur ? cur.audio : null);
    if (via) ae('mode_switch', { mode, via });
    updateSummary();
    if (S.buffer) paintResult();
    if (mode === 'song' && S.buffer && !S.song) schedulePlan('mode', 0);
    if (mode === 'region' && S.regionAct === 'repeat' && S.buffer) {
        if (S.region && !S.repeatResult) buildRepeat('mode');
        else ensureAnalysis().catch(() => {});
    }
}

/* Участок, приехавший вместе с файлом из «Зацикливания аудио» (там он выделен
 * маркерами A/B): сразу «Повторить» на нём, а не выделять заново. */
function takeHint(h) {
    const rep = h && h.hints && h.hints.repeat;
    if (rep && isFinite(rep.start) && isFinite(rep.end) && rep.end > rep.start) S.pendingHint = rep;
}

function applyRepeatHint() {
    const h = S.pendingHint;
    if (!h || !S.buffer) return false;
    S.pendingHint = null;
    const d = S.buffer.duration;
    const start = Math.max(0, Math.min(d, +h.start)), end = Math.max(0, Math.min(d, +h.end));
    if (!(end - start > 0.2)) return false;
    const times = $('es-times');
    if (h.times && times) {
        times.value = String(Math.max(2, Math.min(8, Math.round(+h.times) || 2)));
        put($('es-times-val'), fmtMul(+times.value));
    }
    S.region = { start, end };
    setAct('repeat');
    setMode('region', 'hint');
    if (waveIn) waveIn.set({ region: { ...S.region } });
    return true;
}

/* ── Клавиатура ──────────────────────────────────────────────────────────── */

function onKey(e) {
    if (!S.buffer || e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
    if (window.__keyOwnedByFocus && window.__keyOwnedByFocus(e)) return;
    const work = $('es-work');
    if (!work || work.hidden) return;
    // Только когда прибор в поле зрения: пробел на странице, прокрученной к FAQ,
    // должен листать её, а не включать музыку где-то наверху.
    const r = work.getBoundingClientRect();
    if (r.bottom < 0 || r.top > innerHeight) return;
    if (e.code === 'Space' || e.key === ' ') { e.preventDefault(); $('es-play').click(); }
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const step = e.key === 'ArrowLeft' ? -5 : 5;
        transport.seek(transport.which, Math.max(0, transport.position() + step));
    }
}

/* ── Инициализация ───────────────────────────────────────────────────────── */

function init() {
    const drop = $('es-drop');
    if (!drop) return;
    if (window.__bindUploaderFile) window.__bindUploaderFile(drop, (file) => onFile(file));
    else drop.addEventListener('tool-uploader:file', (e) => onFile(e.detail.file));
    drop.addEventListener('tool-uploader:clear', () => {
        transport.stop();
        clearTimeout(S.planTimer);
        S.file = S.buffer = S.analysis = S.analyzing = S.song = S.regionResult = S.region = S.variants = null;
        S.repeat = S.repeatResult = S.repeatNear = null;
        $('es-work').hidden = true;
        const sample = $('es-sample');
        if (sample) sample.hidden = false;
        setStatus('');
    });

    waveIn = new Waveform($('es-wave-in'), {
        onSeek: (sec, tap) => {
            if (tap) setView('src', 'seek');
            transport.seek('src', sec);
            paintPlayhead(sec, 'src');
        },
        onRegion: (r) => {
            S.region = r;
            // Повтор прежнего участка больше не тот: пересобирается сразу, если
            // выбрано «Повторить», и при возврате к нему — если «Растянуть».
            S.repeat = S.repeatResult = S.repeatNear = null;
            if (r) ae('region_set', { len: +(r.end - r.start).toFixed(1), act: S.regionAct });
            updateSummary();
            if (S.mode === 'region' && S.regionAct === 'repeat') buildRepeat('select');
        },
    });
    waveOut = new Waveform($('es-wave-out'), {
        onSeek: (sec, tap) => {
            if (tap) setView('out', 'seek');
            transport.seek('out', sec);
            paintPlayhead(sec, 'out');
        },
        onSeam: (i) => auditionSeam(i),
    });

    document.querySelectorAll('[data-es-mode]').forEach((b) => b.addEventListener('click', () => setMode(b.getAttribute('data-es-mode'), 'tab')));
    // Нажатие на уже выбранную дорожку — «послушать её»: 121 щелчок в 43 сессиях
    // за 30 дней по «Результату»/«Оригиналу», который и так выбран, не делал ничего.
    document.querySelectorAll('[data-es-view]').forEach((b) => b.addEventListener('click', () => {
        const v = b.getAttribute('data-es-view');
        if (v === S.view && S.buffer && !transport.playing) { ae('ab_replay', { to: v }); $('es-play').click(); return; }
        setView(v, 'ab');
    }));
    document.querySelectorAll('[data-es-fmt]').forEach((b) => b.addEventListener('click', () => setFormat(b.getAttribute('data-es-fmt'), 'pick')));
    $('es-play').addEventListener('click', () => {
        if (!S.buffer) return;
        if (!transport.playing && transport.which !== S.view) { transport.which = S.view; transport.offset = 0; }
        transport.toggle();
        ae('play', { which: S.view });
    });
    $('es-dl').addEventListener('click', download);
    $('es-vid').addEventListener('click', () => { const inp = $('es-len-file'); if (inp && !S.busy) { inp.value = ''; inp.click(); } });
    const sampleBtn = $('es-sample-btn');
    if (sampleBtn) sampleBtn.addEventListener('click', loadSample);
    $('es-stretch').addEventListener('click', runRegion);
    document.querySelectorAll('[data-es-act]').forEach((b) => b.addEventListener('click', () => setAct(b.getAttribute('data-es-act'), 'tab')));
    document.querySelectorAll('[data-es-nudge]').forEach((b) => b.addEventListener('click', () => nudgeEdge(b.getAttribute('data-es-nudge'), +b.getAttribute('data-d'))));
    $('es-suggest').addEventListener('click', () => {
        const n = S.repeatNear;
        if (!n || S.busy) return;
        S.region = { start: n.start, end: n.end };
        ae('repeat_suggest_taken', { stars: n.stars, bars: n.bars });
        buildRepeat('suggest');
    });
    // Файл из другого тула может приехать с участком (tool-handoff.js: hints).
    document.addEventListener('tool-handoff:arrived', (e) => { takeHint(e.detail); if (S.buffer && !S.busy) applyRepeatHint(); });
    if (window.__toolHandoffArrival) takeHint(window.__toolHandoffArrival);
    $('es-len-file').addEventListener('change', (e) => lengthFromFile(e.target.files && e.target.files[0]));

    const tgt = $('es-target');
    tgt.addEventListener('input', () => { updateSummary(); schedulePlan('typed', 450); });
    // Касание выделяет значение, Enter/уход приводит его к виду «м:сс»; отказ
    // возвращает последнее принятое значение, а не то, что стоит в поле.
    (window.__timeFieldQ = window.__timeFieldQ || []).push(() => {
        let last = window.TimeField.parse(tgt.value) || 0;
        window.TimeField.bind(tgt, {
            get: () => { const v = window.TimeField.parse(tgt.value); if (v !== null) last = v; return last; },
            set: (v) => { last = v; tgt.value = fmtTarget(v); updateSummary(); schedulePlan('typed', 0); },
            min: 0, format: fmtTarget, slug: SLUG, field: 'target',
            onInvalid: (raw) => { try { window.__toast && window.__toast(String(t('time_unreadable')).replace(':value', raw), 'error'); } catch (_) {} },
        });
    });
    // ±5 с: нажатие — шаг, удержание — повтор (общий примитив: тап не шагает дважды).
    const stepBy = (d) => () => {
        if (!S.buffer || S.busy) return false;
        const cur = readTarget();
        const next = Math.max(1, Math.round((isFinite(cur) ? cur : S.buffer.duration) + d));
        tgt.value = fmtTarget(next);
        updateSummary();
        schedulePlan('step', 320);
        return true;
    };
    for (const [id, d] of [['es-minus', -5], ['es-plus', 5]]) {
        const b = $(id);
        const step = stepBy(d);
        if (window.__holdRepeat) window.__holdRepeat(b, step, { delay: 400, period: 120 });
        // click — путь клавиатуры и вспомогательных технологий (detail 0); клик
        // того же нажатия пальцем или мышью примитив гасит сам.
        b.addEventListener('click', () => { step(); });
    }

    let timesTimer = 0;
    for (const id of ['es-factor', 'es-rise', 'es-times']) {
        const el = $(id);
        const show = () => {
            put($(id + '-val'), id === 'es-rise'
                ? (parseFloat(el.value) > 0 ? '+' + el.value : '0')
                : fmtMul(parseFloat(el.value)));
            updateSummary();
        };
        el.addEventListener('input', show);
        // Число повторов пересобирает результат (миллисекунды), но не на каждый
        // шаг ползунка под пальцем.
        if (id === 'es-times') {
            el.addEventListener('input', () => {
                clearTimeout(timesTimer);
                timesTimer = setTimeout(() => { if (S.region) buildRepeat('times'); }, 140);
            });
        }
        // Значения запоминаются между визитами общим примитивом настроек
        // (layout определяет его ниже контента — привязка на DOMContentLoaded).
        const bind = () => { if (window.__toolSettings) window.__toolSettings.bind(SLUG, el, id, el.value); show(); };
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind); else bind();
    }

    let fmtSaved = 'mp3';
    try { fmtSaved = localStorage.getItem('es_format') || 'mp3'; } catch (_) {}
    setFormat(fmtSaved);

    document.addEventListener('keydown', onKey);
    let rz = null;
    window.addEventListener('resize', () => {
        clearTimeout(rz);
        rz = setTimeout(() => { waveIn && waveIn.draw(); waveOut && waveOut.draw(); }, 150);
    });
    // Тема поменялась — цвета мест перерисовываются под неё.
    try {
        new MutationObserver(() => { waveIn && waveIn.draw(); waveOut && waveOut.draw(); paintVariants(); })
            .observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    } catch (_) {}

    setMode('song');
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
